import * as React from 'react';
import { cn } from '@/lib/utils';
import negativeIcon from '@/assets/images/icons/negative-icon.svg';
import plusIcon from '@/assets/images/icons/plus-icon.svg';
import arrowIcon from '@/assets/images/icons/arrow-icon.svg';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { EmptyState } from '@/components/custom/empty-state';

const ModalTaxonomyBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-modal-taxonomy-body',
      // Estilo base
      'rounded-md border border-solid border-[#E6E6E6] bg-[#f8f8f8]',
      'relative min-h-[360px] w-full overflow-hidden',
      className
    )}
    {...props}
  />
));
ModalTaxonomyBody.displayName = 'ModalTaxonomyBody';

/**
 * Componente que envolve as colunas de taxonomias (matéria, tópico, subtópico).
 * Implementa a lógica de "slide" horizontal para telas maiores.
 *
 * @param {boolean} isActive - Controla se o wrap está na posição de slide (-50% left).
 */
const ModalTaxonomyWrap = React.forwardRef(({ className, isActive, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-modal-taxonomy-wrap',
      // Estilos base
      'relative w-full transition-all duration-200 ease-in-out',
      'flex flex-wrap items-stretch',
      'left-0 ml-0',
      // Estilos para telas md e maiores
      'md:absolute md:w-[150%]',
      // Comportamento de slide
      isActive ? 'md:left-[-50%]' : 'md:left-0',
      className
    )}
    {...props}
  />
));
ModalTaxonomyWrap.displayName = 'ModalTaxonomyWrap';

/**
 * Componente que representa uma coluna de navegação de taxonomia (Matéria, Tópico, Subtópico).
 * Aplica borda lateral, que é removida no primeiro filho (`first:border-l-0`).
 */
const ModalTaxonomyCollumn = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'c-modal-taxonomy-collumn',
        // Estilos base
        'w-full p-2 md:w-1/3',
        // Borda
        'border-l border-solid border-[#E6E6E6] first:border-l-0',
        'relative max-h-[360px] overflow-x-hidden overflow-y-scroll text-sm font-light',
        className
      )}
      {...props}
    />
  );
});
ModalTaxonomyCollumn.displayName = 'ModalTaxonomyCollumn';

/**
 * Componente de cabeçalho para as colunas de navegação de taxonomia.
 */
const ModalTaxonomyHeader = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'c-modal-taxonomy-header',
        // Estilos
        'bg-[#4A4A49] text-white',
        'text-xs',
        'font-sans',
        'pb-[10px] pl-[25px] pr-0 pt-[15px]',
        'm-[-11px] mb-2',
        'border-none',
        className
      )}
      {...props}
    />
  );
});
ModalTaxonomyHeader.displayName = 'ModalTaxonomyHeader';

const ModalTaxonomyCTA = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('c-modal-taxonomy-cta', className)}
      {...props}
    />
  );
});
ModalTaxonomyCTA.displayName = 'ModalTaxonomyCTA';

/**
 * Componente que representa um único item clicável na lista de taxonomias.
 *
 * @param {boolean} isActive - Se verdadeiro, aplica o background de ativo (#EDEDED).
 */
const ModalTaxonomyItem = React.forwardRef(({ className, isActive, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'c-modal-taxonomy-item',
        // Estilos
        'w-full',
        'my-[1px] px-[6px] py-[7px] pr-[10px]',
        'cursor-pointer rounded-md transition-colors duration-150',
        'hover:bg-[#EDEDED]',
        'flex items-center gap-2',
        // Estado Ativo
        isActive && 'bg-[#EDEDED]',
        className
      )}
      {...props}
    />
  );
});

ModalTaxonomyItem.displayName = 'ModalTaxonomyItem';

/**
 * Componente que envolve o ícone de adição/remoção (+/-) dentro de um item da lista.
 */
const ModalTaxonomyIcon = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'c-modal-taxonomy-icon',
        // Estilos
        'w-[12px]',
        'flex-shrink-0 cursor-pointer text-left',
        'flex items-center justify-center',
        className
      )}
      {...props}
    />
  );
});
ModalTaxonomyIcon.displayName = 'ModalTaxonomyIcon';

const ModalTaxonomyText = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn(
        'c-modal-taxonomy-text',
        // Estilos
        'mb-0 flex flex-grow items-center justify-between gap-2',
        className
      )}
      {...props}
    />
  );
});
ModalTaxonomyText.displayName = 'ModalTaxonomyText';

/**
 * Componente que exibe o nome da Matéria, Tópico ou Subtópico.
 * Aplica o estilo de fonte e muda a cor para a cor primária quando ativo.
 *
 * @param {boolean} isActive - Se verdadeiro, aplica a cor primária.
 */
const ModalTaxonomyName = React.forwardRef(({ className, isActive, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn(
        'c-modal-taxonomy-name',
        // Estilos
        'flex-grow text-[.85em] leading-tight',
        // Estado Ativo
        isActive && 'text-primary',
        className
      )}
      {...props}
    />
  );
});
ModalTaxonomyName.displayName = 'ModalTaxonomyName';

const ModalTaxonomyArrow = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn(
        'c-modal-taxonomy-arrow',
        // Estilos
        'flex flex-shrink-0 items-center',
        className
      )}
      {...props}
    />
  );
});
ModalTaxonomyArrow.displayName = 'ModalTaxonomyArrow';

/**
 * Componente de botão para retornar ao nível de taxonomia anterior.
 *
 * @param {boolean} isActive - Se verdadeiro, o botão é visível.
 */
const ModalTaxonomyButtonBack = React.forwardRef(({ className, isActive, onClick, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-label="Voltar para a seleção de nível anterior"
      className={cn(
        'c-modal-taxonomy-button-back',
        // Estilos
        'absolute left-0 top-[5px]',
        'cursor-pointer',
        { hidden: !isActive },
        'z-10 p-1 text-gray-500 transition-colors hover:text-gray-900',
        className
      )}
      {...props}
    >
      <ChevronLeft className="h-4 w-4 text-white" />
    </button>
  );
});
ModalTaxonomyButtonBack.displayName = 'ModalTaxonomyButtonBack';

function ModalTaxonomy(props) {
  const { t } = useTranslation();
  const { closeModal, selectedTaxonomies = [], setFieldValue, open, onOpenChange } = props;
  const [taxonomies, setTaxonomies] = React.useState(props.taxonomies);
  const [topics, setTopics] = React.useState([]);
  const [subTopics, setSubTopics] = React.useState([]);
  const [isWrapActive, setIsWrapActive] = React.useState(false);

  const getTaxonomyParents = (allList, currentList, taxonomyId) => {
    const items = [];
    currentList.map(t => {
      if (t['taxonomy-children']) {
        if (t['taxonomy-children'].map(({ id }) => id).includes(taxonomyId)) {
          items.push(t);
          items.push(getTaxonomyParents(allList, allList, t.id));
        } else {
          items.push(getTaxonomyParents(allList, t['taxonomy-children'], taxonomyId));
        }
      }
    });
    return items;
  };

  const selectTaxonomy = (taxonomy, currentList, nextList) => {
    deactivateTaxonomies(currentList);
    taxonomy.isActive = true;

    let newTopics = taxonomy['taxonomy-children'];
    deactivateTaxonomies(nextList);
    selectedTaxonomies.forEach(item => {
      if (newTopics.includes(item)) {
        let index = newTopics.indexOf(item);
        let topic = newTopics[index];
        topic.isActive = true;
      }
    });

    setTaxonomies([...taxonomies]);
    if (nextList === 'subTopics') {
      setSubTopics([...newTopics]);
    } else {
      setTopics([...newTopics]);
    }
    setIsWrapActive(nextList === 'subTopics');
  };

  const selectTopic = topic => {
    const selectedTopic = selectedTaxonomies?.filter(t => t.id === topic.id).length === 1;

    if (!selectedTopic) {
      const parents = getTaxonomyParents(props.taxonomies, props.taxonomies, topic.id).flat(20);
      const allItems = [...(selectedTaxonomies ?? []), topic, ...parents];
      const uniqueItems = Array.from(new Set(allItems.map(item => item.id))).map(id => allItems.find(item => item.id === id));
      setFieldValue('selectedTaxonomies', uniqueItems);
    } else {
      setFieldValue(
        'selectedTaxonomies',
        selectedTaxonomies?.filter(t => t.id !== topic.id)
      );
    }

    setTaxonomies([...taxonomies]);
  };

  const deactivateTaxonomies = listName => {
    if (listName === 'topics') {
      topics.forEach(i => {
        i.isActive = false;
      });
      setSubTopics([...topics]);
    } else {
      taxonomies.forEach(i => {
        i.isActive = false;
      });
      setTaxonomies([...taxonomies]);
    }
  };

  const unWrap = () => {
    setIsWrapActive(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent>
        <DialogTitle>{t('textsCommon.titleChooseSubject')}</DialogTitle>

        <ModalTaxonomyBody>
          <ModalTaxonomyWrap isActive={isWrapActive}>
            <ModalTaxonomyCollumn>
              <ModalTaxonomyHeader>{t('textsCommon.matter')}</ModalTaxonomyHeader>

              {taxonomies &&
                taxonomies.map(taxonomy => (
                  <ModalTaxonomyItem
                    key={taxonomy.id}
                    isActive={taxonomy.isActive}
                  >
                    <ModalTaxonomyIcon onClick={() => selectTopic(taxonomy)}>
                      <img
                        alt={`ícone de ${selectedTaxonomies?.filter(t => t.id === taxonomy.id).length === 1 ? 'remover' : 'adicionar'}`}
                        src={selectedTaxonomies?.filter(t => t.id === taxonomy.id).length === 1 ? negativeIcon : plusIcon}
                      />
                    </ModalTaxonomyIcon>
                    <ModalTaxonomyText onClick={() => selectTaxonomy(taxonomy, 'taxonomies', 'topics')}>
                      <ModalTaxonomyName isActive={taxonomy.isActive}>{taxonomy.name}</ModalTaxonomyName>
                      <ModalTaxonomyArrow>
                        <img
                          alt=""
                          src={arrowIcon}
                        />
                      </ModalTaxonomyArrow>
                    </ModalTaxonomyText>
                  </ModalTaxonomyItem>
                ))}
            </ModalTaxonomyCollumn>

            <ModalTaxonomyCollumn>
              <ModalTaxonomyButtonBack
                isActive={isWrapActive}
                onClick={unWrap}
              />

              <ModalTaxonomyHeader>{t('textsCommon.topic')}</ModalTaxonomyHeader>

              {topics && topics.length > 0 ? (
                topics.map(topic => (
                  <ModalTaxonomyItem isActive={topic.isActive}>
                    <ModalTaxonomyIcon onClick={() => selectTopic(topic)}>
                      <img
                        alt={`ícone de ${selectedTaxonomies.filter(t => t.id === topic.id).length === 1 ? 'remover' : 'adicionar'}`}
                        src={selectedTaxonomies.filter(t => t.id === topic.id).length === 1 ? negativeIcon : plusIcon}
                      />
                    </ModalTaxonomyIcon>
                    <ModalTaxonomyText onClick={() => selectTaxonomy(topic, 'topics', 'subTopics')}>
                      <ModalTaxonomyName isActive={topic.isActive}>{topic.name.substring(0, 35)}</ModalTaxonomyName>
                      <ModalTaxonomyArrow>
                        <img
                          alt=""
                          src={arrowIcon}
                        />
                      </ModalTaxonomyArrow>
                    </ModalTaxonomyText>
                  </ModalTaxonomyItem>
                ))
              ) : (
                <EmptyState
                  variant="plain"
                  type="select"
                  text={`${t('textsCommon.textSelectMatterToAdd')}..`}
                />
              )}
            </ModalTaxonomyCollumn>

            <ModalTaxonomyCollumn>
              <ModalTaxonomyHeader>Subtópico</ModalTaxonomyHeader>

              {subTopics && subTopics.length > 0 ? (
                subTopics.map(topic => (
                  <ModalTaxonomyItem
                    key={topic.id}
                    isActive={topic.isActive}
                    onClick={() => selectTopic(topic)}
                  >
                    <ModalTaxonomyIcon>
                      <img
                        alt={`ícone de ${selectedTaxonomies.filter(t => t.id === topic.id).length === 1 ? 'remover' : 'adicionar'}`}
                        src={selectedTaxonomies.filter(t => t.id === topic.id).length === 1 ? negativeIcon : plusIcon}
                      />
                    </ModalTaxonomyIcon>
                    <ModalTaxonomyName isActive={topic.isActive}>{topic.name.substring(0, 40)}</ModalTaxonomyName>
                  </ModalTaxonomyItem>
                ))
              ) : (
                <EmptyState
                  variant="plain"
                  type="select"
                  text={t('textsCommon.textSelectMatterToAdd')}
                />
              )}
            </ModalTaxonomyCollumn>
          </ModalTaxonomyWrap>
        </ModalTaxonomyBody>

        <ModalTaxonomyCTA>
          <Button
            size="sm"
            type="submit"
            onClick={closeModal}
          >
            {t('button.confirm')}
          </Button>
        </ModalTaxonomyCTA>
      </DialogContent>
    </Dialog>
  );
}

export { ModalTaxonomy };
